import { IconButton, Stack, styled } from '@mui/material';
import { FocusEvent, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { getPortableHtmlFromDisplayHtml } from 'src/components/TextWithUI/display-html-to-portable-html';
import { useCopyToClipboard } from 'src/utils/hooks';
import { getPlainTextFromMessageHtml } from 'src/utils/text-parsing';

export const MESSAGE_TOOLBAR_CLASS = 'message-toolbar';

const COPIED_FEEDBACK_DURATION_MS = 1500;
const TOOLBAR_ICON_SIZE_PX = 14;
const TOOLBAR_EDGE_OFFSET = '0.375rem';
const TOOLBAR_HEIGHT = '1.75rem';

/*
 * A pointer click leaves the button focused, so a `:focus-within` reveal would keep the toolbar up
 * after the pointer left the message. `:focus-visible` is what tells keyboard focus from that click.
 */
const isKeyboardFocus = (element: EventTarget): boolean =>
  element instanceof HTMLElement && element.matches(':focus-visible');

const ToolbarRoot = styled(Stack, {
  name: 'MessageToolbar',
  shouldForwardProp: (prop) => prop !== 'isRevealed',
})<{ isRevealed: boolean }>(({ theme, isRevealed }) => ({
  position: 'absolute',
  /* 6px from the top, but never past the card's vertical middle, so a one-line card keeps it centered. */
  top: `min(${TOOLBAR_EDGE_OFFSET}, calc(50% - ${TOOLBAR_HEIGHT} / 2))`,
  right: TOOLBAR_EDGE_OFFSET,
  zIndex: 3,
  alignItems: 'center',
  gap: '0.0625rem',
  padding: '0.125rem',
  backgroundColor: theme.palette.custom.OV.sys.surfaceContainer,
  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  borderRadius: '0.5rem',
  opacity: isRevealed ? 1 : 0,
  transition: 'opacity 0.12s ease',
}));

const ToolbarButton = styled(IconButton, {
  name: 'MessageToolbarButton',
  shouldForwardProp: (prop) => prop !== 'isCopied',
})<{ isCopied?: boolean }>(({ theme, isCopied }) => ({
  width: '1.375rem',
  height: '1.375rem',
  padding: 0,
  borderRadius: '0.375rem',
  color: isCopied
    ? theme.palette.custom.OV.sys.success
    : theme.palette.custom.OV.sys.onSurfaceVariant,
  transition: 'background-color 0.12s ease, color 0.12s ease',

  '&:hover': {
    backgroundColor: isCopied
      ? 'transparent'
      : theme.palette.custom.OV.sys.surfaceContainerHigh,
    color: isCopied
      ? theme.palette.custom.OV.sys.success
      : theme.palette.custom.OV.sys.onBackground,
  },
}));

interface MessageToolbarProps {
  messageHtml: string;
  showMenu: boolean;
  isMenuOpen: boolean;
  onMenuClick: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const MessageToolbar = ({
  messageHtml,
  showMenu,
  isMenuOpen,
  onMenuClick,
}: MessageToolbarProps) => {
  const { copyToClipboard } = useCopyToClipboard();
  const [copiedAt, setCopiedAt] = useState<number | null>(null);
  const [hasKeyboardFocus, setHasKeyboardFocus] = useState(false);

  const displayedText = useMemo(() => getPlainTextFromMessageHtml(messageHtml), [messageHtml]);
  const canCopy = displayedText.length > 0;

  useEffect(() => {
    if (copiedAt === null) {
      return;
    }

    const handle = window.setTimeout(() => setCopiedAt(null), COPIED_FEEDBACK_DURATION_MS);

    return () => window.clearTimeout(handle);
  }, [copiedAt]);

  const handleFocus = useCallback((event: FocusEvent<HTMLDivElement>) => {
    setHasKeyboardFocus(isKeyboardFocus(event.target));
  }, []);

  const handleBlur = useCallback(() => {
    setHasKeyboardFocus(false);
  }, []);

  const handleCopy = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const portableHtml = getPortableHtmlFromDisplayHtml(messageHtml);
      await copyToClipboard({
        plainText: getPlainTextFromMessageHtml(portableHtml),
        html: portableHtml,
      });
      setCopiedAt(Date.now());
    },
    [copyToClipboard, messageHtml],
  );

  if (!canCopy && !showMenu) {
    return null;
  }

  const isCopied = copiedAt !== null;

  return (
    <ToolbarRoot
      direction="row"
      className={MESSAGE_TOOLBAR_CLASS}
      isRevealed={isMenuOpen || hasKeyboardFocus}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {canCopy ? (
        <Tooltip title={isCopied ? 'Copied' : 'Copy message text'}>
          <ToolbarButton
            aria-label="Copy message text"
            data-testid="copy-message-text"
            isCopied={isCopied}
            onClick={handleCopy}
          >
            <DynamicIcon
              library="lucide"
              name={isCopied ? 'check' : 'copy'}
              size={TOOLBAR_ICON_SIZE_PX}
            />
          </ToolbarButton>
        </Tooltip>
      ) : null}
      {showMenu ? (
        <Tooltip title="More">
          <ToolbarButton
            aria-label="More actions"
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            onClick={onMenuClick}
          >
            <DynamicIcon library="lucide" name="more-vertical" size={TOOLBAR_ICON_SIZE_PX} />
          </ToolbarButton>
        </Tooltip>
      ) : null}
    </ToolbarRoot>
  );
};
